// [목적] 온보딩 진행 상태를 BE 에 디바운스 PATCH 로 자동 동기화하는 훅 — SLE-FE-03 Sub 3
// [이유] 온보딩 중 입력이 바뀔 때마다 즉시 PATCH 하면 네트워크 과부하. 500ms 디바운스로 묶어서
//         마지막 변경값만 전송. stepIndex 변경(단계 전환)은 즉시 flush — race condition 완화(AD-3).
// [사이드 이팩트] enabled=false(비로그인 또는 온보딩 완료 사용자) 시 PATCH 호출 0회.
//                 PATCH 실패 시 지수백오프 3회 재시도 (500ms→1.5s→4.5s).
//                 마지막 재시도도 실패하면 lastError 세팅 + 비차단 Snackbar 노출.
//                 컴포넌트 언마운트 시 pending 타이머 취소.
// [수정 시 고려사항] lodash.debounce 미사용 — package.json 에 lodash 의존 없음(2026-06-01 확인).
//                   lodash 추가 결정 시 자체 setTimeout 헬퍼를 lodash.debounce 로 교체 가능.
//                   재시도 지수는 BASE_RETRY_DELAYS 배열로 관리 — 3회 고정. 필요 시 배열 확장.

import { useCallback, useEffect, useRef, useState } from 'react';
import { patchOnboardingProgress } from '../api/onboarding.client.js';
import type { OnboardingProgressPatch } from '../api/onboarding.client.js';

// ─── 상수 ─────────────────────────────────────────────────────────────────────

/** [목적] 스냅샷 변경 디바운스 지연 (ms) */
const DEBOUNCE_MS = 500;

/**
 * [목적] 지수백오프 재시도 딜레이 배열 (ms) — 3회: 500ms, 1.5s, 4.5s
 * [이유] 일시적 네트워크 장애에 과부하 없이 자연 복구 유도
 * [수정 시 고려사항] 배열 길이 = 최대 재시도 횟수
 */
const BASE_RETRY_DELAYS = [500, 1500, 4500] as const;

// ─── 훅 옵션 / 반환 타입 ──────────────────────────────────────────────────────

export interface UseOnboardingProgressSyncOpts {
  /** enabled=false 이면 PATCH 호출 0회 (비로그인 또는 onboardedAt 존재) */
  enabled: boolean;
  /** 현재 온보딩 단계 인덱스 — 변경 시 즉시 flush */
  stepIndex: number;
  /** 현재 온보딩 입력 스냅샷 — 변경 시 500ms 디바운스 PATCH */
  snapshot: Record<string, unknown>;
}

export interface UseOnboardingProgressSyncReturn {
  /** 즉시 PATCH 전송 (디바운스 타이머 취소 후 동기적 flush) */
  flush: () => Promise<void>;
  /** 마지막 PATCH 성공 시각 */
  lastSyncedAt: Date | null;
  /** 마지막 재시도 실패 에러 (재시도 모두 소진 시 세팅, 이후 성공 시 null 초기화) */
  lastError: Error | null;
}

// ─── 훅 ──────────────────────────────────────────────────────────────────────

/**
 * [목적] 온보딩 진행 자동 동기화 훅
 * [이유] SLE-FE-03 Sub 3 — snapshot 변경 500ms 디바운스 / stepIndex 변경 즉시 flush / 지수백오프 재시도
 * [사이드 이팩트] enabled=false 시 전체 비활성. Snackbar 는 마지막 재시도 실패 1회만.
 * [수정 시 고려사항] snapshot 을 직렬화해 이전값과 비교 — 동일값 재전송 방지.
 *                   stepIndex 변경 감지는 ref 비교로 이전값과 비교.
 */
export function useOnboardingProgressSync(
  opts: UseOnboardingProgressSyncOpts,
): UseOnboardingProgressSyncReturn {
  const { enabled, stepIndex, snapshot } = opts;

  // ── 내부 ref ─────────────────────────────────────────────────────────────
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevStepRef = useRef<number>(stepIndex);
  const prevSnapshotJsonRef = useRef<string>(JSON.stringify(snapshot));
  // enabled 가 false 일 때 pending flush 가 실행되지 않도록 최신값 동기화
  const enabledRef = useRef(enabled);
  const stepIndexRef = useRef(stepIndex);
  const snapshotRef = useRef(snapshot);

  useEffect(() => {
    enabledRef.current = enabled;
  });
  useEffect(() => {
    stepIndexRef.current = stepIndex;
  });
  useEffect(() => {
    snapshotRef.current = snapshot;
  });

  // ── 상태 ─────────────────────────────────────────────────────────────────
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);

  // ── Snackbar 상태 (비차단 에러 알림) ─────────────────────────────────────
  // MUI Snackbar 를 직접 렌더하지 않고, DOM CustomEvent 로 전파해 최상단 Snackbar 가 처리.
  // OnboardingPage 기존 Snackbar(toastOpen/toastMessage) 와 별도 채널로 비차단 에러 노출.
  const dispatchErrorSnackbar = useCallback((message: string): void => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('gangwon:snackbar', { detail: { message, severity: 'warning' } }),
      );
    }
  }, []);

  // ── 핵심 PATCH 실행 (재시도 포함) ──────────────────────────────────────────
  /**
   * [목적] PATCH 요청 1회 실행 — 지수백오프 재시도 포함
   * [이유] 일시적 네트워크/서버 오류에 자동 복구
   * [사이드 이팩트] 모든 재시도 소진 시 lastError 세팅 + 비차단 Snackbar
   * [수정 시 고려사항] enabled=false 체크는 호출 직전 enabledRef 로 재확인 — 비로그인 전환 race 방지
   */
  const sendPatch = useCallback(
    async (payload: OnboardingProgressPatch): Promise<void> => {
      // 호출 시점 재확인 — 비로그인 전환 race 방지
      if (!enabledRef.current) return;

      let lastErr: Error | null = null;

      for (let attempt = 0; attempt < BASE_RETRY_DELAYS.length + 1; attempt++) {
        try {
          await patchOnboardingProgress(payload);
          setLastSyncedAt(new Date());
          setLastError(null);
          return;
        } catch (err: unknown) {
          lastErr = err instanceof Error ? err : new Error(String(err));

          // 마지막 시도(attempt === BASE_RETRY_DELAYS.length)이면 재시도 없이 종료
          if (attempt >= BASE_RETRY_DELAYS.length) break;

          // 아직 재시도 남음 — 딜레이 후 재시도
          const delay = BASE_RETRY_DELAYS[attempt];
          await new Promise<void>((resolve) => setTimeout(resolve, delay));

          // 재시도 직전 enabled 재확인
          if (!enabledRef.current) return;
        }
      }

      // 모든 재시도 소진
      if (lastErr) {
        setLastError(lastErr);
        dispatchErrorSnackbar('진행 상태 저장에 실패했습니다. 나중에 다시 시도됩니다.');
      }
    },
    [dispatchErrorSnackbar],
  );

  // ── flush (즉시 전송) ─────────────────────────────────────────────────────
  /**
   * [목적] 디바운스 타이머 취소 후 현재 스냅샷을 즉시 PATCH
   * [이유] stepIndex 변경(단계 전환) 또는 외부 요청 시 즉시 서버 반영 필요
   * [사이드 이팩트] 타이머 취소 — 이후 debounce 로 중복 전송 없음
   * [수정 시 고려사항] enabled=false 시 no-op
   */
  const flush = useCallback(async (): Promise<void> => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (!enabledRef.current) return;
    await sendPatch({
      stepIndex: stepIndexRef.current,
      snapshot: snapshotRef.current,
    });
  }, [sendPatch]);

  // ── stepIndex 변경 → 즉시 flush ──────────────────────────────────────────
  useEffect(() => {
    if (!enabled) {
      prevStepRef.current = stepIndex;
      return;
    }
    if (stepIndex !== prevStepRef.current) {
      prevStepRef.current = stepIndex;
      // 즉시 flush — 비동기지만 반환값 무시(fire-and-forget). 에러는 sendPatch 내부에서 처리
      void flush();
    }
    // flush 는 useCallback 으로 안정 — 의존성 추가 불필요
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, enabled]);

  // ── snapshot 변경 → 500ms 디바운스 PATCH ─────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    const currentJson = JSON.stringify(snapshot);

    // 이전값과 동일하면 전송 불필요 (초기 마운트 포함)
    if (currentJson === prevSnapshotJsonRef.current) return;
    prevSnapshotJsonRef.current = currentJson;

    // 기존 타이머 취소
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
    }

    // 새 디바운스 타이머 설정
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      void sendPatch({
        stepIndex: stepIndexRef.current,
        snapshot: snapshotRef.current,
      });
    }, DEBOUNCE_MS);
  }, [snapshot, enabled, sendPatch]);

  // ── 언마운트 시 타이머 정리 ───────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  return { flush, lastSyncedAt, lastError };
}
